// WAAP AI-WAF → Compliance Validation (HTTP Protocol Compliance).
//
// HYBRID of the advanced-strategy family: READ via the advanced endpoints
// (GET /waf_advanced_strategies?type=8 + GET /waf_advanced_strategy/:id) — so the
// list/detail reuse useAdvancedStrategies / useAdvancedStrategy with COMPLIANCE_TYPE
// — but WRITE through a separate `waf_compliance` surface (eagle_waf/advanced):
//
//   POST   /waap/domains/:id/compliance        → portal POST   /waf_compliance
//   PUT    /waap/domains/:id/compliance/:cid   → portal PUT    /waf_compliance (id in body)
//   DELETE /waap/domains/:id/compliance/:cid   → portal DELETE /waf_compliance/:cid

import { useMutation, useQueryClient, type UseMutationOptions } from '@tanstack/react-query';
import { serviceApiV3 } from '@/api/client';
import { advancedKeys, type AdvancedStrategy } from './advanced-strategy';

/** The strategy `type` for HTTP Protocol Compliance rules. */
export const COMPLIANCE_TYPE = 8;

function basePath(domainId: string): string {
  return `/waap/domains/${domainId}/compliance`;
}

function invalidate(qc: ReturnType<typeof useQueryClient>, domainId: string) {
  qc.invalidateQueries({ queryKey: advancedKeys.strategies(domainId, COMPLIANCE_TYPE) });
}

/** Create a compliance rule. */
export function useCreateCompliance<TConfig = Record<string, unknown>>(
  domainId: string,
  options?: UseMutationOptions<unknown, unknown, AdvancedStrategy<TConfig>>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: AdvancedStrategy<TConfig>) => {
      const res = await serviceApiV3.post(basePath(domainId), body);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      invalidate(qc, domainId);
      options?.onSuccess?.(...args);
    },
  });
}

/** Update a compliance rule. */
export function useUpdateCompliance<TConfig = Record<string, unknown>>(
  domainId: string,
  id: number | string,
  options?: UseMutationOptions<unknown, unknown, AdvancedStrategy<TConfig>>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: AdvancedStrategy<TConfig>) => {
      const res = await serviceApiV3.put(`${basePath(domainId)}/${id}`, body);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      invalidate(qc, domainId);
      qc.invalidateQueries({ queryKey: advancedKeys.strategy(domainId, COMPLIANCE_TYPE, id) });
      options?.onSuccess?.(...args);
    },
  });
}

/** Delete a compliance rule. */
export function useDeleteCompliance(
  domainId: string,
  options?: UseMutationOptions<unknown, unknown, number | string>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number | string) => {
      const res = await serviceApiV3.delete(`${basePath(domainId)}/${id}`);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      invalidate(qc, domainId);
      options?.onSuccess?.(...args);
    },
  });
}

// ---- Service-scoped twins (account-level, work with zero websites) ----------

function serviceBasePath(serviceId: string): string {
  return `/services/${serviceId}/waap/compliance`;
}

function invalidateService(qc: ReturnType<typeof useQueryClient>, serviceId: string) {
  // Compliance reads reuse advanced-strategies with COMPLIANCE_TYPE, so invalidate
  // the service-scoped advanced strategies cache for that type.
  qc.invalidateQueries({
    queryKey: ['waap', 'advanced', 'service', serviceId, 'strategies', COMPLIANCE_TYPE],
  });
}

/** Create a compliance rule, service-scoped. */
export function useCreateServiceCompliance<TConfig = Record<string, unknown>>(
  serviceId: string,
  options?: UseMutationOptions<unknown, unknown, AdvancedStrategy<TConfig>>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: AdvancedStrategy<TConfig>) => {
      const res = await serviceApiV3.post(serviceBasePath(serviceId), body);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      invalidateService(qc, serviceId);
      options?.onSuccess?.(...args);
    },
  });
}

/** Update a compliance rule, service-scoped. */
export function useUpdateServiceCompliance<TConfig = Record<string, unknown>>(
  serviceId: string,
  id: number | string,
  options?: UseMutationOptions<unknown, unknown, AdvancedStrategy<TConfig>>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: AdvancedStrategy<TConfig>) => {
      const res = await serviceApiV3.put(`${serviceBasePath(serviceId)}/${id}`, body);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      invalidateService(qc, serviceId);
      options?.onSuccess?.(...args);
    },
  });
}

/** Delete a compliance rule, service-scoped. */
export function useDeleteServiceCompliance(
  serviceId: string,
  options?: UseMutationOptions<unknown, unknown, number | string>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number | string) => {
      const res = await serviceApiV3.delete(`${serviceBasePath(serviceId)}/${id}`);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      invalidateService(qc, serviceId);
      options?.onSuccess?.(...args);
    },
  });
}
